if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        /*
         * Listen for slide changes on both mobile and desktop.
         * This must run even when thumbnails are hidden.
         */
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 150)
          );
        }

        /*
         * Thumbnail behavior only runs when thumbnails exist.
         */
        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (!button) return;

              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            });

          if (
            this.dataset.desktopLayout.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        }

        /*
         * Start the first active media correctly after page load.
         */
        window.setTimeout(() => {
          const activeMedia =
            this.elements.viewer?.querySelector('.is-active') ||
            this.elements.viewer?.querySelector('[data-media-id]');

          if (activeMedia) {
            this.playActiveMedia(activeMedia);
          }
        }, 300);
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;

        if (!activeMedia) return;

        /*
         * Mark the swiped-to slide as active.
         */
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        /*
         * Automatically load and play the active video.
         */
        this.playActiveMedia(activeMedia);

        /*
         * Update the thumbnail when thumbnails are available.
         */
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          // Do not scroll vertically if the media is already visible.
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        const thumbnailButton = thumbnail.querySelector('button');

        if (thumbnailButton) {
          thumbnailButton.setAttribute('aria-current', 'true');
        }

        if (
          this.elements.thumbnails.isSlideVisible &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            'false'
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          window.setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              'true'
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        /*
         * Pause videos or models on all other slides.
         */
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        /*
         * Shopify product videos start inside deferred-media.
         * Load the video automatically instead of waiting for a click.
         */
        const deferredMedia =
          activeItem.querySelector('deferred-media') ||
          activeItem.querySelector('.deferred-media');

        if (deferredMedia && deferredMedia.loadContent) {
          deferredMedia.loadContent(false);
        }

        /*
         * Give Shopify a moment to insert the video from its template,
         * then apply the mobile autoplay requirements.
         */
        const startVideo = () => {
          const video = activeItem.querySelector('video');

          if (!video) return false;

          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.autoplay = true;
          video.playsInline = true;

          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('autoplay', '');
          video.setAttribute('playsinline', '');

          video.play().catch(() => {
            /*
             * Some browsers need a second attempt after the video
             * has loaded enough data.
             */
            video.addEventListener(
              'canplay',
              () => {
                video.play().catch(() => {});
              },
              { once: true }
            );
          });

          return true;
        };

        if (startVideo()) return;

        window.setTimeout(() => {
          if (startVideo()) return;

          window.setTimeout(startVideo, 300);
        }, 100);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}